import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { pulseboardApi } from '../../services/api';

const ADDRESS_RE = /^0x[a-fA-F0-9]{40}$/;
const REFRESH_MS = 5 * 60 * 1000;
const HOLD_MS = 3000;
const HOLD_SLOP_PX = 10;
const INTERVAL_CHOICES = [15, 30, 60, 120, 240, 360, 720, 1440];
const FRAMES = [['m5', '5m'], ['h1', '1h'], ['h6', '6h'], ['h24', '24h']];

// Shared class strings, matching the rest of the site
const CARD = 'bg-white border border-gray-200 rounded-lg';
const BTN = 'px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed';
const BTN_PRIMARY = 'px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 transition disabled:opacity-60';
const INPUT = 'w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';
const LABEL = 'block text-sm font-medium text-gray-700 mb-1';
const NUM = 'font-mono tabular-nums';

const EMPTY_SETTINGS = {
  wallets: [],
  excludedTokens: [],
  enabled: false,
  intervalMinutes: 60,
  alertPct: null,
  lastSentAt: null,
  lastCheckedAt: null,
  lastTotalUsd: null,
  baselineUsd: null,
  baselineAt: null,
  lastError: null,
  telegramConfigured: false,
};

/** Axios errors carry the server's { error } payload - surface that, not "Request failed". */
function errorText(err, fallback) {
  return err?.response?.data?.error || err?.message || fallback;
}

function parseWallets(text) {
  const errors = [];
  const seen = new Set();
  const wallets = [];
  for (const raw of text.split(/\n+/)) {
    const line = raw.trim();
    if (!line) continue;
    if (!ADDRESS_RE.test(line)) {
      errors.push(`Not a wallet address: ${line.slice(0, 18)}`);
      continue;
    }
    const key = line.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    wallets.push(line);
  }
  if (wallets.length > 5) errors.push('Up to 5 wallets.');
  return { wallets: wallets.slice(0, 5), errors };
}

/** Telegram credentials live in the server config, so sending is all-or-nothing. */
function canSend(s) {
  return !!s.telegramConfigured;
}

function formatUsd(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  if (n >= 1000) return `$${n.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
  if (n >= 1) return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
  if (n >= 0.01) return `$${n.toFixed(4)}`;
  if (n >= 0.001) return `$${n.toFixed(6)}`.replace(/0+$/, '').replace(/\.$/, '');
  return `$${n.toFixed(8)}`.replace(/0+$/, '').replace(/\.$/, '');
}

/** Sub-thousandth prices render as $0.0<sup>n</sup>rest instead of a wall of zeros. */
function micros(n) {
  if (n == null || n >= 0.001) return null;
  const frac = n.toFixed(12).split('.')[1] || '';
  let zeros = 0;
  for (const ch of frac) {
    if (ch === '0') zeros += 1;
    else break;
  }
  const rest = frac.slice(zeros).replace(/0+$/, '').slice(0, 4);
  if (zeros >= 3 && rest) return { zeros, rest };
  return null;
}

function compactUsd(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  if (abs >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  if (abs >= 1) return `$${n.toFixed(0)}`;
  return formatUsd(n);
}

/** Portfolio values are money, not prices - always two decimals. */
function formatMoney(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatPct(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  return `${n > 0 ? '+' : ''}${n.toFixed(Math.abs(n) >= 10 ? 1 : 2)}%`;
}

function formatTokenAmount(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  if (abs >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${(n / 1e3).toFixed(2)}K`;
  if (abs >= 100) return n.toFixed(0);
  if (abs >= 1) return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
  if (abs === 0) return '0';
  return n.toPrecision(4).replace(/0+$/, '').replace(/\.$/, '');
}

function formatTime(ts) {
  const d = new Date(ts);
  return [d.getUTCHours(), d.getUTCMinutes(), d.getUTCSeconds()]
    .map((x) => String(x).padStart(2, '0'))
    .join(':') + ' UTC';
}

function formatCountdown(ms) {
  const total = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/** Dropdown label: "15 minutes", "1 hour", "12 hours". */
function formatDuration(minutes) {
  if (minutes % 60 === 0) {
    const hours = minutes / 60;
    return `${hours} hour${hours === 1 ? '' : 's'}`;
  }
  return `${minutes} minutes`;
}

/** Sentence form: "every 15 min", "every hour", "every 2 hours". */
function formatEvery(minutes) {
  if (minutes % 60 === 0) {
    const hours = minutes / 60;
    return hours === 1 ? 'hour' : `${hours} hours`;
  }
  return `${minutes} min`;
}

const moveTone = (v) => (v == null || v === 0 ? 'flat' : v > 0 ? 'up' : 'down');
const MOVE_TEXT = { up: 'text-green-600', down: 'text-red-600', flat: 'text-gray-500' };
const MOVE_PILL = { up: 'bg-green-100 text-green-700', down: 'bg-red-100 text-red-700', flat: 'bg-gray-100 text-gray-600' };
const MOVE_BAR = { up: 'bg-green-500', down: 'bg-red-500', flat: 'bg-gray-300' };

function Usd({ value, className = '' }) {
  const micro = typeof value === 'number' ? micros(value) : null;
  if (value == null || !Number.isFinite(value)) {
    return <span className={`${NUM} ${className}`}>—</span>;
  }
  if (!micro) return <span className={`${NUM} ${className}`}>{formatUsd(value)}</span>;
  return (
    <span className={`${NUM} ${className}`}>
      $0.0<sup className="text-[0.6em] font-semibold">{micro.zeros}</sup>{micro.rest}
    </span>
  );
}

function Money({ value, className = '' }) {
  return <span className={`${NUM} ${className}`}>{formatMoney(value)}</span>;
}

function Chip({ value, label }) {
  const tone = moveTone(value);
  return (
    <span className={`inline-block px-2 py-0.5 text-xs font-semibold rounded-full tabular-nums ${MOVE_PILL[tone]}`}>
      {label ? `${label} ` : ''}{formatPct(value)}
    </span>
  );
}

function Avatar({ symbol, imageUrl, lg }) {
  const size = lg ? 'w-12 h-12 text-sm' : 'w-9 h-9 text-xs';
  return (
    <span className={`${size} shrink-0 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center overflow-hidden font-semibold text-gray-500`}>
      {imageUrl ? <img src={imageUrl} alt="" className="w-full h-full object-cover" /> : (symbol || '?').slice(0, 3)}
    </span>
  );
}

/**
 * Press and hold for three seconds to fire `onHold`.
 *
 * Pointer events cover mouse, touch and pen in one path. A drag past a few
 * pixels (a scroll on a phone) or a lifted finger cancels the press, and a
 * completed hold swallows the click that follows so holding a price card does
 * not also open its DexScreener link.
 */
function useHold(onHold, enabled = true) {
  const [progress, setProgress] = useState(0);
  const press = useRef({ raf: 0, startedAt: 0, pointerId: null, x: 0, y: 0, firedAt: 0 });

  const stop = useCallback(() => {
    const p = press.current;
    if (p.raf) cancelAnimationFrame(p.raf);
    p.raf = 0;
    p.pointerId = null;
    setProgress(0);
  }, []);

  useEffect(() => stop, [stop]);

  const onPointerDown = useCallback((e) => {
    if (!enabled) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const p = press.current;
    p.pointerId = e.pointerId;
    p.x = e.clientX;
    p.y = e.clientY;
    p.startedAt = Date.now();

    const tick = () => {
      const elapsed = Date.now() - p.startedAt;
      if (elapsed >= HOLD_MS) {
        p.firedAt = Date.now();
        stop();
        onHold();
        return;
      }
      setProgress(elapsed / HOLD_MS);
      p.raf = requestAnimationFrame(tick);
    };
    p.raf = requestAnimationFrame(tick);
  }, [enabled, onHold, stop]);

  const onPointerMove = useCallback((e) => {
    const p = press.current;
    if (p.pointerId == null || e.pointerId !== p.pointerId) return;
    if (Math.abs(e.clientX - p.x) > HOLD_SLOP_PX || Math.abs(e.clientY - p.y) > HOLD_SLOP_PX) stop();
  }, [stop]);

  const cancel = useCallback(() => {
    if (press.current.pointerId != null) stop();
  }, [stop]);

  const onClick = useCallback((e) => {
    if (Date.now() - press.current.firedAt < 800) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  // Spread straight onto the element - the title is what makes the gesture discoverable.
  const handlers = enabled ? {
    title: 'Press and hold for 3 seconds to exclude this token',
    onPointerDown,
    onPointerMove,
    onPointerUp: cancel,
    onPointerCancel: cancel,
    onPointerLeave: cancel,
    onClick,
    onContextMenu: (e) => e.preventDefault(),
    onDragStart: (e) => e.preventDefault(),
  } : {};

  return { progress, handlers };
}

/** Class + inline style that make an element hold-friendly on touch screens. */
const HOLD_CLASS = 'select-none';
const HOLD_STYLE = { WebkitTouchCallout: 'none', touchAction: 'manipulation' };

/** Fill bar pinned to the bottom of a held card or tile. */
function HoldBar({ progress }) {
  if (!progress) return null;
  return (
    <span className="absolute inset-x-0 bottom-0 h-1 bg-primary-100 overflow-hidden pointer-events-none rounded-b-lg">
      <span className="block h-full bg-primary-600" style={{ width: `${Math.round(progress * 100)}%` }} />
    </span>
  );
}

/** Same feedback for table rows, where an absolute overlay will not lay out. */
function holdRowStyle(progress) {
  if (!progress) return undefined;
  const stop = `${Math.round(progress * 100)}%`;
  return { backgroundImage: `linear-gradient(to right, #dbeafe ${stop}, transparent ${stop})` };
}

export default function Pulseboard() {
  const [settings, setSettings] = useState(EMPTY_SETTINGS);
  const [hydrated, setHydrated] = useState(false);
  const [snapshot, setSnapshot] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [frame, setFrame] = useState('h24');
  const [now, setNow] = useState(null);
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [sendStatus, setSendStatus] = useState('');
  const [excludeTarget, setExcludeTarget] = useState(null);
  const [excludeBusy, setExcludeBusy] = useState(false);
  const [excludeError, setExcludeError] = useState('');
  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  const walletKey = settings.wallets.join(',');

  useEffect(() => {
    let cancelled = false;
    pulseboardApi.getSettings()
      .then((res) => { if (!cancelled) setSettings({ ...EMPTY_SETTINGS, ...res.data }); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setHydrated(true); });
    return () => { cancelled = true; };
  }, []);

  const refreshPrices = useCallback(async () => {
    try {
      const res = await pulseboardApi.prices();
      setSnapshot(res.data);
    } catch (err) {
      setSnapshot((prev) => ({
        ok: false,
        fetchedAt: Date.now(),
        source: 'PulseX via DexScreener',
        tokens: prev?.tokens || [],
        error: errorText(err, 'Price feed unavailable'),
      }));
    }
  }, []);

  const refreshPortfolio = useCallback(async () => {
    const current = settingsRef.current;
    if (!current.wallets.length) {
      setPortfolio(null);
      return;
    }
    setScanning(true);
    try {
      const res = await pulseboardApi.portfolio(current.wallets);
      setPortfolio(res.data);
    } catch (err) {
      setPortfolio({
        ok: false,
        fetchedAt: Date.now(),
        wallets: current.wallets,
        totalUsd: 0,
        chains: [],
        error: errorText(err, 'Wallet scan failed'),
      });
    } finally {
      setScanning(false);
    }
  }, []);

  const sendReport = useCallback(async () => {
    const current = settingsRef.current;
    if (!current.wallets.length) {
      setSendStatus('Add a wallet address first.');
      setOpen(true);
      return;
    }
    if (!canSend(current)) {
      setSendStatus('Telegram is not configured on the server.');
      return;
    }
    setSending(true);
    setSendStatus('Sending…');
    try {
      // Credentials stay on the server - it sends through the app's Telegram bot.
      const res = await pulseboardApi.telegram({ wallets: current.wallets });
      if (res.data.ok) {
        if (res.data.snapshot) setPortfolio(res.data.snapshot);
        setSendStatus(`Sent ${compactUsd(res.data.totalUsd ?? res.data.snapshot?.totalUsd)} to Telegram`);
      } else {
        setSendStatus(res.data.error || 'Telegram send failed');
      }
    } catch (err) {
      setSendStatus(errorText(err, 'Send failed'));
    } finally {
      setSending(false);
    }
  }, []);

  /** Both feeds are filtered server-side, so a change means re-pulling them. */
  const saveExclusions = useCallback(async (next) => {
    setExcludeBusy(true);
    setExcludeError('');
    try {
      const res = await pulseboardApi.updateExclusions(next);
      setSettings({ ...EMPTY_SETTINGS, ...res.data });
      setExcludeTarget(null);
      refreshPrices();
      refreshPortfolio();
    } catch (err) {
      setExcludeError(errorText(err, 'Could not save excluded tokens'));
    } finally {
      setExcludeBusy(false);
    }
  }, [refreshPrices, refreshPortfolio]);

  const requestExclude = useCallback((token) => {
    if (!token?.key) return;
    setExcludeError('');
    setExcludeTarget({ key: token.key, symbol: token.symbol || 'this token' });
  }, []);

  const confirmExclude = useCallback(() => {
    const target = excludeTarget;
    if (!target) return;
    const current = settingsRef.current.excludedTokens || [];
    if (current.some((e) => e.key === target.key)) {
      setExcludeTarget(null);
      return;
    }
    saveExclusions([...current, { key: target.key, symbol: target.symbol }]);
  }, [excludeTarget, saveExclusions]);

  const restoreToken = useCallback((key) => {
    saveExclusions((settingsRef.current.excludedTokens || []).filter((e) => e.key !== key));
  }, [saveExclusions]);

  useEffect(() => {
    refreshPrices();
    const id = setInterval(refreshPrices, REFRESH_MS);
    return () => clearInterval(id);
  }, [refreshPrices]);

  useEffect(() => {
    if (!hydrated) return undefined;
    refreshPortfolio();
    const id = setInterval(refreshPortfolio, REFRESH_MS);
    return () => clearInterval(id);
  }, [hydrated, walletKey, refreshPortfolio]);

  useEffect(() => {
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const tokens = useMemo(() => snapshot?.tokens || [], [snapshot]);
  const featured = tokens.find((t) => t.id === 'pls') || tokens[0];
  const rest = tokens.filter((t) => t !== featured);
  const updatedAt = snapshot?.fetchedAt || Date.now();
  const remaining = now == null ? REFRESH_MS : Math.max(0, updatedAt + REFRESH_MS - now);
  const stats = useMemo(() => {
    const volume = tokens.reduce((s, t) => s + (t.volume24h || 0), 0);
    const liq = tokens.reduce((s, t) => s + (t.liquidityUsd || 0), 0);
    const up = tokens.filter((t) => (t.change?.h24 || 0) > 0).length;
    const down = tokens.filter((t) => (t.change?.h24 || 0) < 0).length;
    return { volume, liq, up, down, count: tokens.length };
  }, [tokens]);
  const maxBar = Math.max(1, ...tokens.map((t) => Math.abs(t.change?.[frame] || 0)));

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pulseboard</h1>
          <p className="text-sm text-gray-500">PulseChain prices and wallet totals</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1.5 text-xs text-gray-500 mr-1">
            <span className={`w-2 h-2 rounded-full ${snapshot?.ok ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
            {snapshot?.ok ? 'Live' : 'Stale'}
            <span className={`${NUM} text-gray-400`}>{formatCountdown(remaining)}</span>
          </span>
          <button type="button" className={BTN} onClick={() => setOpen(true)} disabled={!hydrated}>Wallets</button>
          <button type="button" className={BTN} onClick={() => { refreshPrices(); refreshPortfolio(); }}>Refresh</button>
        </div>
      </div>

      <WatcherNote settings={settings} />

      <Portfolio
        settings={settings}
        snapshot={portfolio}
        scanning={scanning}
        sending={sending}
        sendStatus={sendStatus}
        onOpen={() => setOpen(true)}
        onSend={sendReport}
        onHold={requestExclude}
      />

      {snapshot?.error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
          {snapshot.error}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        {[
          ['24h volume', compactUsd(stats.volume)],
          ['Liquidity', compactUsd(stats.liq)],
          ['Up / down', `${stats.up} / ${stats.down}`],
          ['Tracked', `${stats.count} coins`],
        ].map(([k, v]) => (
          <div className={`${CARD} px-4 py-3`} key={k}>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{k}</p>
            <p className={`${NUM} text-lg text-gray-900 mt-0.5`}>{v}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        {featured ? <PriceCard token={featured} featured onHold={requestExclude} /> : null}
        <section className={`${CARD} p-4 lg:col-span-2`}>
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <h2 className="font-semibold text-gray-900 text-sm">Relative move</h2>
              <p className="text-xs text-gray-500">Change across core coins</p>
            </div>
            <div className="flex gap-1">
              {FRAMES.map(([k, l]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setFrame(k)}
                  className={`px-2 py-0.5 text-xs rounded-md transition ${
                    frame === k ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2.5">
            {tokens.map((t) => <MoveRow key={t.id} token={t} frame={frame} maxBar={maxBar} onHold={requestExclude} />)}
            {!tokens.length && (
              <p className="text-sm text-gray-400 py-4 text-center">
                {snapshot?.excludedCount ? 'Every tracked coin is excluded.' : 'Loading prices…'}
              </p>
            )}
          </div>
        </section>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
        {rest.map((t) => <PriceCard key={t.id} token={t} onHold={requestExclude} />)}
      </div>

      <section className={`${CARD} overflow-x-auto mb-4`}>
        <table className="w-full text-sm">
          <thead>
            <tr>
              {['Token', 'Price', '24h', 'Volume', 'Liquidity'].map((h, i) => (
                <th key={h} className={`px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide ${i ? 'text-right' : 'text-left'}`}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tokens.map((t) => <TokenRow key={t.id} token={t} onHold={requestExclude} />)}
          </tbody>
        </table>
      </section>

      <ExcludedTokens
        excluded={settings.excludedTokens || []}
        portfolio={portfolio}
        busy={excludeBusy}
        error={excludeTarget ? '' : excludeError}
        onRestore={restoreToken}
        onRestoreAll={() => saveExclusions([])}
      />

      <p className="text-xs text-gray-400 leading-relaxed">
        Updated {formatTime(updatedAt)} · {snapshot?.source || 'PulseX via DexScreener'}. Quotes come from the deepest
        PulseX pool for each token and refresh every five minutes. Wallet totals scan PulseChain and Ethereum, skip
        dust and unpriced tokens, and are not financial advice. Press and hold any token for three seconds to exclude
        it from this page and every total.
      </p>

      {excludeTarget && (
        <ExcludeModal
          target={excludeTarget}
          busy={excludeBusy}
          error={excludeError}
          onCancel={() => { setExcludeTarget(null); setExcludeError(''); }}
          onConfirm={confirmExclude}
        />
      )}

      {open && hydrated && (
        <SettingsModal
          settings={settings}
          sending={sending}
          sendStatus={sendStatus}
          onClose={() => setOpen(false)}
          onSaved={(saved) => { setSettings({ ...EMPTY_SETTINGS, ...saved }); setOpen(false); }}
          onSend={sendReport}
        />
      )}
    </div>
  );
}

/** What the server-side watcher is doing right now. */
function WatcherNote({ settings }) {
  if (!settings.telegramConfigured) {
    return (
      <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg mb-4 text-sm">
        Telegram is not configured on the server — set <code className="font-mono text-xs">TELEGRAM_BOT_TOKEN</code> and{' '}
        <code className="font-mono text-xs">TELEGRAM_CHAT_ID</code> to send portfolio reports.
      </div>
    );
  }
  if (!settings.enabled) return null;

  const checked = settings.lastCheckedAt ? new Date(settings.lastCheckedAt).toLocaleString() : null;
  return (
    <div className="bg-primary-50 border border-primary-200 text-primary-800 px-4 py-3 rounded-lg mb-4 text-sm">
      Checking every {formatEvery(settings.intervalMinutes)} —{' '}
      {settings.alertPct
        ? <>Telegram alert when the total moves ±{settings.alertPct}% from {compactUsd(settings.baselineUsd)}</>
        : <>a Telegram report every check</>}
      {checked ? ` · last checked ${checked}` : ''}
      {settings.lastTotalUsd != null ? ` · last total ${compactUsd(settings.lastTotalUsd)}` : ''}
      {settings.lastError ? <> · <span className="text-red-600">{settings.lastError}</span></> : null}
    </div>
  );
}

function PriceCard({ token, featured, onHold }) {
  const { progress, handlers } = useHold(() => onHold(token), !!onHold && !!token.key);
  const inner = (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <Avatar symbol={token.symbol} imageUrl={token.imageUrl} lg={featured} />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <h2 className="font-semibold text-gray-900 text-sm">{token.symbol}</h2>
              {featured && (
                <span className="inline-block px-2 py-0.5 text-xs font-semibold rounded-full bg-primary-100 text-primary-700">
                  Native
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 truncate">{token.role}</p>
          </div>
        </div>
        <Chip value={token.change?.h24} />
      </div>

      <Usd value={token.priceUsd} className={`block mt-4 text-gray-900 ${featured ? 'text-3xl' : 'text-2xl'}`} />
      {featured && token.priceUsd ? (
        <p className={`${NUM} text-xs text-gray-400 mt-1`}>
          1 USD = {Math.round(1 / token.priceUsd).toLocaleString('en-US')} PLS
        </p>
      ) : null}

      <div className="flex flex-wrap gap-1.5 mt-3">
        {FRAMES.map(([k, l]) => <Chip key={k} value={token.change?.[k]} label={l} />)}
      </div>

      <dl className="grid grid-cols-3 gap-2 mt-4 pt-3 border-t border-gray-100 text-xs">
        {[['Volume', token.volume24h], ['Liquidity', token.liquidityUsd], ['Mkt cap', token.marketCap]].map(([k, v]) => (
          <div key={k}>
            <dt className="text-gray-400">{k}</dt>
            <dd className={`${NUM} text-gray-700 mt-0.5`}>{compactUsd(v)}</dd>
          </div>
        ))}
      </dl>
    </>
  );

  if (token.pairUrl) {
    return (
      <a className={`${CARD} ${HOLD_CLASS} relative p-4 block hover:border-primary-200 hover:shadow-sm transition no-underline`}
        style={HOLD_STYLE} href={token.pairUrl} target="_blank" rel="noopener noreferrer" {...handlers}>
        {inner}
        <HoldBar progress={progress} />
      </a>
    );
  }
  return (
    <article className={`${CARD} ${HOLD_CLASS} relative p-4`} style={HOLD_STYLE} {...handlers}>
      {inner}
      <HoldBar progress={progress} />
    </article>
  );
}

/** One coin's bar in the relative-move list. */
function MoveRow({ token, frame, maxBar, onHold }) {
  const { progress, handlers } = useHold(() => onHold(token), !!onHold && !!token.key);
  const v = token.change?.[frame] || 0;
  const tone = moveTone(v);
  return (
    <div
      className={`${HOLD_CLASS} relative grid grid-cols-[3.5rem_1fr_4rem] gap-2 items-center text-xs py-1 rounded-md`}
      style={{ ...HOLD_STYLE, ...holdRowStyle(progress) }}
      {...handlers}
    >
      <span className={`${NUM} text-gray-700`}>{token.symbol}</span>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${MOVE_BAR[tone]}`} style={{ width: `${Math.min(100, (Math.abs(v) / maxBar) * 100)}%` }} />
      </div>
      <span className={`${NUM} text-right ${MOVE_TEXT[tone]}`}>{formatPct(token.change?.[frame])}</span>
    </div>
  );
}

/** One coin's row in the price table. */
function TokenRow({ token, onHold }) {
  const { progress, handlers } = useHold(() => onHold(token), !!onHold && !!token.key);
  return (
    <tr className={HOLD_CLASS} style={{ ...HOLD_STYLE, ...holdRowStyle(progress) }} {...handlers}>
      <td className="px-3 py-3 border-t border-gray-100">
        <div className="flex items-center gap-2.5">
          <Avatar symbol={token.symbol} imageUrl={token.imageUrl} />
          <div className="min-w-0">
            <p className="font-semibold text-gray-900 text-sm">{token.symbol}</p>
            <p className="text-xs text-gray-400 truncate">{token.name}</p>
          </div>
        </div>
      </td>
      <td className="px-3 py-3 border-t border-gray-100 text-right text-gray-900"><Usd value={token.priceUsd} /></td>
      <td className="px-3 py-3 border-t border-gray-100 text-right"><Chip value={token.change?.h24} /></td>
      <td className={`px-3 py-3 border-t border-gray-100 text-right text-gray-600 ${NUM}`}>{compactUsd(token.volume24h)}</td>
      <td className={`px-3 py-3 border-t border-gray-100 text-right text-gray-600 ${NUM}`}>{compactUsd(token.liquidityUsd)}</td>
    </tr>
  );
}

/** One wallet holding, holdable to drop it out of the totals. */
function HoldingTile({ holding, onHold }) {
  const { progress, handlers } = useHold(() => onHold(holding), !!onHold && !!holding.key);
  return (
    <div
      className={`${HOLD_CLASS} relative flex items-center justify-between gap-3 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2`}
      style={HOLD_STYLE}
      {...handlers}
    >
      <div className="flex items-center gap-2.5 min-w-0">
        <Avatar symbol={holding.symbol} imageUrl={holding.imageUrl} />
        <div className="min-w-0">
          <p className="font-semibold text-gray-900 text-sm truncate">{holding.symbol}</p>
          <p className={`${NUM} text-xs text-gray-400`}>{formatTokenAmount(holding.amount)}</p>
        </div>
      </div>
      <Money value={holding.valueUsd} className="text-sm text-gray-900" />
      <HoldBar progress={progress} />
    </div>
  );
}

function Portfolio({ settings, snapshot, scanning, sending, sendStatus, onOpen, onSend, onHold }) {
  if (!settings.wallets.length) {
    return (
      <section className={`${CARD} p-6 mb-4 text-center`}>
        <h2 className="font-semibold text-gray-900 text-sm">Your wallets</h2>
        <p className="text-sm text-gray-500 mt-1 max-w-md mx-auto">
          Add addresses to price every token you hold on PulseChain and Ethereum, then get the USD total in Telegram.
        </p>
        <button type="button" className={`${BTN_PRIMARY} mt-4`} onClick={onOpen}>Add wallets</button>
      </section>
    );
  }

  if (!snapshot && scanning) {
    return (
      <section className={`${CARD} p-6 mb-4 flex flex-col items-center gap-3`}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        <p className="text-sm text-gray-500">Scanning PulseChain and Ethereum…</p>
      </section>
    );
  }
  if (!snapshot) return null;

  return (
    <section className={`${CARD} p-4 mb-4`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Wallet total</p>
          <p className={`${NUM} text-3xl text-gray-900 mt-0.5`}>{formatMoney(snapshot.totalUsd)}</p>
          <p className="text-xs text-gray-400 mt-1">
            {snapshot.wallets?.length || 0} wallet{snapshot.wallets?.length === 1 ? '' : 's'} · {formatTime(snapshot.fetchedAt)}
            {scanning ? ' · scanning' : ''}
            {snapshot.excludedCount ? ` · ${snapshot.excludedCount} excluded (${formatMoney(snapshot.excludedUsd || 0)})` : ''}
          </p>
        </div>
        <button
          type="button"
          className={BTN}
          onClick={onSend}
          disabled={!canSend(settings) || sending || scanning}
          title={canSend(settings) ? '' : 'Telegram is not configured on the server'}
        >
          {sending ? 'Sending…' : 'Send to Telegram'}
        </button>
      </div>

      {sendStatus && <p className="text-xs text-gray-500 mt-2">{sendStatus}</p>}
      {snapshot.error && <p className="text-xs text-red-600 mt-2">{snapshot.error}</p>}

      {snapshot.chains?.map((chain) => (
        <div key={chain.id}>
          <div className="flex items-center gap-2 mt-4 mb-2">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{chain.label}</span>
            <span className={`${NUM} text-xs text-gray-700`}>{formatMoney(chain.totalUsd)}</span>
            {chain.error && <span className="text-xs text-red-600">{chain.error}</span>}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {(chain.holdings || []).filter((h) => h.native || (h.valueUsd || 0) > 0).slice(0, 8).map((h) => (
              <HoldingTile holding={h} onHold={onHold} key={`${h.chain}-${h.address}`} />
            ))}
          </div>
          {chain.hidden > 0 && (
            <p className="text-xs text-gray-400 mt-1.5">{chain.hidden} more hidden as dust or unpriced</p>
          )}
        </div>
      ))}
    </section>
  );
}

/** Confirmation for a completed press-and-hold. */
function ExcludeModal({ target, busy, error, onCancel, onConfirm }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={() => { if (!busy) onCancel(); }} />
      <div className="relative bg-white rounded-2xl shadow-xl border border-gray-100 p-6 max-w-sm w-full">
        <h2 className="text-lg font-semibold text-gray-900">Exclude {target.symbol}?</h2>
        <p className="text-sm text-gray-500 mt-2">
          {target.symbol} disappears from the Pulseboard and stops counting towards your wallet total, the chain
          totals and the Telegram reports. Restore it any time from Excluded tokens.
        </p>
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm mt-3">{error}</div>
        )}
        <div className="flex flex-wrap gap-3 pt-5">
          <button type="button" className={BTN_PRIMARY} onClick={onConfirm} disabled={busy}>
            {busy ? 'Excluding…' : `Exclude ${target.symbol}`}
          </button>
          <button
            type="button"
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/** The user's hidden tokens, with the live value they are keeping out of the total. */
function ExcludedTokens({ excluded, portfolio, busy, error, onRestore, onRestoreAll }) {
  if (!excluded.length) return null;
  const held = new Map((portfolio?.excludedHoldings || []).map((h) => [h.key, h]));
  const heldUsd = portfolio?.excludedUsd || 0;

  return (
    <section className={`${CARD} p-4 mb-4`}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div>
          <h2 className="font-semibold text-gray-900 text-sm">Excluded tokens</h2>
          <p className="text-xs text-gray-500">
            {excluded.length} hidden from the board and the totals
            {heldUsd > 0 ? ` · ${formatMoney(heldUsd)} left out` : ''}
          </p>
        </div>
        <button type="button" className={BTN} onClick={onRestoreAll} disabled={busy}>Restore all</button>
      </div>
      {error && <p className="text-xs text-red-600 mb-2">{error}</p>}
      <div className="flex flex-wrap gap-2">
        {excluded.map((e) => {
          const holding = held.get(e.key);
          return (
            <span
              className="inline-flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-full pl-3 pr-1.5 py-1"
              key={e.key}
            >
              <span className="text-sm font-semibold text-gray-700">{e.symbol || 'Token'}</span>
              {holding?.valueUsd ? (
                <Money value={holding.valueUsd} className="text-xs text-gray-400" />
              ) : null}
              <button
                type="button"
                onClick={() => onRestore(e.key)}
                disabled={busy}
                className="text-xs font-medium text-primary-600 hover:text-primary-700 px-2 py-0.5 rounded-full hover:bg-primary-50 transition disabled:opacity-50"
              >
                Restore
              </button>
            </span>
          );
        })}
      </div>
    </section>
  );
}

function SettingsModal({ settings, sending, sendStatus, onClose, onSaved, onSend }) {
  const [walletsText, setWalletsText] = useState(settings.wallets.join('\n'));
  const [enabled, setEnabled] = useState(settings.enabled);
  const [intervalMinutes, setIntervalMinutes] = useState(settings.intervalMinutes || 60);
  const [alertPct, setAlertPct] = useState(settings.alertPct == null ? '' : String(settings.alertPct));
  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const walletsChanged =
    parseWallets(walletsText).wallets.join(',').toLowerCase() !== settings.wallets.join(',').toLowerCase();

  async function save() {
    const parsed = parseWallets(walletsText);
    if (parsed.errors.length) {
      setErrors(parsed.errors);
      return;
    }
    const pct = alertPct.trim();
    if (pct && !(Number(pct) >= 0 && Number(pct) <= 1000)) {
      setErrors(['Alert threshold must be a percentage between 0 and 1000.']);
      return;
    }
    setSaving(true);
    setErrors([]);
    try {
      const res = await pulseboardApi.updateSettings({
        wallets: parsed.wallets,
        enabled,
        intervalMinutes,
        alertPct: pct === '' ? null : Number(pct),
      });
      onSaved(res.data);
    } catch (err) {
      const details = err?.response?.data?.details;
      setErrors(details?.length ? details : [errorText(err, 'Could not save settings')]);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl border border-gray-100 p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-semibold text-gray-900">Wallet watcher</h2>
        <p className="text-sm text-gray-500 mt-1 mb-4">
          One 0x address per line, up to five. Each is checked on PulseChain and Ethereum.
          Reports go to the Telegram chat configured for this app.
        </p>

        <div className="space-y-4">
          <div>
            <label className={LABEL} htmlFor="pb-wallets">Wallet addresses</label>
            <textarea
              id="pb-wallets"
              rows={4}
              value={walletsText}
              onChange={(e) => setWalletsText(e.target.value)}
              spellCheck={false}
              placeholder="0x…"
              className={`${INPUT} font-mono text-xs resize-y`}
            />
          </div>

          <label className="flex items-start gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              disabled={!settings.telegramConfigured}
              className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">
              Check the portfolio on a schedule and report to Telegram. The server keeps checking with this tab closed.
              {!settings.telegramConfigured && (
                <span className="text-amber-700"> (Telegram is not configured on the server.)</span>
              )}
            </span>
          </label>

          {enabled && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={LABEL} htmlFor="pb-interval">Check every</label>
                <select
                  id="pb-interval"
                  value={intervalMinutes}
                  onChange={(e) => setIntervalMinutes(Number(e.target.value))}
                  className={INPUT}
                >
                  {INTERVAL_CHOICES.map((m) => <option key={m} value={m}>{formatDuration(m)}</option>)}
                </select>
              </div>
              <div>
                <label className={LABEL} htmlFor="pb-alert">Notify when total moves (%)</label>
                <input
                  id="pb-alert"
                  type="number"
                  min="0"
                  max="1000"
                  step="0.1"
                  value={alertPct}
                  onChange={(e) => setAlertPct(e.target.value)}
                  placeholder="blank = every check"
                  className={INPUT}
                />
              </div>
            </div>
          )}

          {enabled && (
            <p className="text-xs text-gray-400 leading-relaxed">
              Measured against the total at the last alert, so a slow drift only notifies once it crosses the
              threshold. After each alert the comparison restarts from the new total.
              {walletsChanged && ' Changing the wallets resets it on the next check.'}
            </p>
          )}

          {errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {errors.map((e) => <p key={e}>{e}</p>)}
            </div>
          )}
          {sendStatus && <p className="text-xs text-gray-500">{sendStatus}</p>}
        </div>

        <div className="flex flex-wrap gap-3 pt-5">
          <button type="button" className={BTN_PRIMARY} onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            className={BTN}
            onClick={onSend}
            disabled={sending || !settings.wallets.length || !settings.telegramConfigured}
          >
            Send now
          </button>
          <button type="button" className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
